const moment = require("moment");
const TimeFormat = require("../../../shared/TimeFormat");
const AT = require("../vault/ActionTypes");
const ErrorUtil = require("../../../shared/ErrorUtil");
const CanvasUtil = require("./CanvasUtil");
const {
	atPage,
	atUser,
	atCurrResult,
	atCurrProblem,
} = require("../vault/StateCursors");

// Basic diagnostic data fixed on page load.
const baVersion = global.BA_PACKAGE_VERSION || "<unknown>";
const startDateNow = Date.now();
const startUserTime = TimeFormat.getDateTimeString(moment());
const userAgent =
	(global.navigator && global.navigator.userAgent) || "<unknown>";
const browser = require("detect-browser").detect();

// Functions getting dynamic diagnostic data.

const RECENT_ACTION_COUNT = 20;

const getValueIfShort = function(value, limit = 50) {
	if (JSON.stringify(value) > limit) {
		return "<long>";
	}
	return value;
};

// Pass an entry e in the debug action history.
const getReducedAction = function(e, long) {
	// When building this object, we try to assign more useful keys first, since
	// that determines how it gets stringified in logs.
	const aInfo = {};

	if (long) {
		aInfo.action = e.action;
	} else {
		aInfo.type = e.action && e.action.type;
		if (e.action && e.action.error) {
			aInfo.error = e.action.error;
		}
		if (aInfo.type === AT.SET_PAGE) {
			aInfo.page = e.action.pageState;
			aInfo.browser = e.action.fromBrowser;
		}
		if (aInfo.type === AT.SET_CONNECTED) {
			aInfo.conn = e.action.connected;
			if (e.action.pingingSocket) {
				aInfo.ping = true;
			}
		}
		if (aInfo.type === AT.SHOW_MESSAGE || aInfo.type === AT.SHOW_CONFIRM) {
			aInfo.messageType = e.action.messageType;
		}
		if (aInfo.type === AT.CONFIRM_PAGE_NAVIGATION) {
			aInfo.confirmType = e.action.confirmType;
		}
		if (aInfo.type === AT.STORAGE_UPDATED) {
			aInfo.key = e.action.key;
		}
		if (aInfo.type === AT.SUBMIT_TRIAL || aInfo.type === AT.SAVE_TEST_ANSWER) {
			aInfo.trial = getValueIfShort(e.action.currentTrialAns);
			if (e.action.forceSubmit) {
				aInfo.forceSubmit = e.action.forceSubmit;
			}
			if (e.action.forceSave) {
				aInfo.forceSave = e.action.forceSave;
			}
			if (e.action.giveUp) {
				aInfo.giveUp = true;
			}
		}
		if (aInfo.type === AT.CHANGE_TRIAL_STATE) {
			aInfo.change = e.action.changeType;
		}
		if (aInfo.type === AT.SET_SHOW_INSTRUCTIONS) {
			aInfo.show = e.action.showInstructions;
		}
	}

	if (e.suppressedBy) {
		aInfo.suppressedBy = e.suppressedBy;
		aInfo.sDN = e.updateDateNow - startDateNow;
		aInfo.sPN = e.updatePerfNow;
	}
	if (e.customEvents && e.customEvents.length) {
		aInfo.events = e.customEvents.map(ev => ev && ev.name);
	}
	if (e.emitted) {
		aInfo.emit = true;
	}
	if (e.dispatchDateNow) {
		aInfo.dDN = e.dispatchDateNow - startDateNow;
		aInfo.dPN = e.dispatchPerfNow;
	}
	if (e.updateDateNow) {
		aInfo.uDN = e.updateDateNow - startDateNow;
		aInfo.uPN = e.updatePerfNow;
	}

	return aInfo;
};

const getVaultActions = function(vaultLogger, long) {
	const actions = vaultLogger.actionHistory;
	if (!Array.isArray(actions)) {
		return null;
	}
	return actions
		.filter(Boolean)
		.slice(Math.max(actions.length - RECENT_ACTION_COUNT, 0))
		.map(e => getReducedAction(e, long));
};

const getVaultState = function(vaultLogger, long) {
	const state = vaultLogger.lastVaultState;
	if (!state) {
		return null;
	}
	// Format for both short and long
	const abbrevState = {
		page: atPage(state).get(),
		userID: atUser(state).getIn("userID"),
		resultID: atCurrResult(state).getIn("resultID"),
		problemID: atCurrProblem(state).getIn("problemID"),
	};
	return abbrevState;
};

// The exported diagnostics functions.

const get = function({long = false, vaultLogger = null} = {}) {
	const returnVal = {};
	try {
		// If this try block throws, we want as much data as possible, so we set
		// properties one at a time.
		returnVal.userAgent = userAgent;
		returnVal.browserName = browser && browser.name;
		returnVal.browserVersion = browser && browser.version;
		returnVal.osName = browser && browser.os;
		returnVal.baVersion = baVersion;
		returnVal.startDateNow = startDateNow;
		returnVal.startUserTime = startUserTime;
		returnVal.url = global.location && global.location.href;
		returnVal.dateNow = Date.now();
		returnVal.perfNow =
			global.performance && Math.floor(global.performance.now());
		returnVal.currUserTime = TimeFormat.getDateTimeString(moment());
		returnVal.canvasCounts = CanvasUtil.getCanvasCountInfo();
		if (long) {
			returnVal.errors = ErrorUtil.getLoggedErrors();
		}
		if (vaultLogger) {
			const currAction =
				vaultLogger.ongoingAction || vaultLogger.getLastErroredAction();
			returnVal.ongoingAction =
				currAction && getReducedAction(currAction, long);
			returnVal.finalState = getVaultState(vaultLogger, long);
			returnVal.actions = getVaultActions(vaultLogger, long);
		}
	} catch (ex) {
		returnVal.diagnosticsError = ex.message;
	}
	return returnVal;
};

module.exports = {
	get,
	getVaultState,
	getReducedAction,
};
